<template>
  <div
    v-if="open"
    class="overlay-dialog"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="overlay-backdrop"
      aria-hidden="true"
      @click="$emit('close')"
    />
    <div
      class="dialog-card"
      role="document"
    >
      <header class="dialog-header">
        <h3 class="h2">
          Entlassen bestätigen
        </h3>
      </header>

      <div class="dialog-body">
        <div class="form-grid">
          <label class="field w-35">
            <span class="label">Datum</span>
            <flat-pickr
              v-model="dateValue"
              :config="dateOpts"
              class="input"
              aria-label="Datum wählen"
              @on-change="onDateChangeFp"
            />
          </label>

          <label class="field w-25">
            <span class="label">Uhrzeit</span>
            <flat-pickr
              v-model="timeString"
              :config="timeOpts"
              class="input"
              aria-label="Uhrzeit wählen"
              @on-change="onTimeChangeFp"
            />
          </label>

          <label class="field w-40">
            <span class="label">Grund</span>
            <select
              v-model.number="selectedReasonId"
              class="input"
              @change="onReasonChange"
            >
              <option
                v-for="reason in releaseReasons"
                :key="reason.id"
                :value="reason.id"
              >
                {{ reason.title }}
              </option>
            </select>
          </label>
        </div>
        <div class="">
          <label class="field field-textarea">
            <span class="label">Notiz (optional)</span>
            <textarea
              v-model.trim="noteText"
              class="input textarea"
              rows="2"
            />
          </label>
        </div>

        <p class="mb-2">
          Soll
          <strong>{{ studentName || 'Schüler' }}</strong>
          wirklich am
          <strong>{{ dateDisplay }}</strong>
          um
          <strong>{{ timeString }}</strong>
          Uhr wegen
          <strong>{{ currentReasonTitle }}</strong>
          entlassen werden?
        </p>
      </div>

      <footer class="dialog-actions">
        <button
          class="btn"
          type="button"
          :disabled="submitting"
          @click="$emit('close')"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-warn"
          type="button"
          :disabled="submitting"
          @click="confirmRelease"
        >
          <i
            class="pi pi-sign-out"
            aria-hidden="true"
          />
          <span style="margin-left:6px;">{{ submitting ? 'Entlasse…' : 'Entlassen speichern' }}</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';
import {presenceApi} from '@/services/api/presence';
import {useToast} from 'primevue/usetoast';

const props = defineProps({
  open: {type: Boolean, default: false},
  // Erwartet Objekt mit mind. student_id|id|studentId und optional student_name|studentName|label
  student: {type: Object, default: () => null},
  occurredAt: {type: String, default: ''},
  // Fallbacks (werden durch Auswahl im Dialog überschrieben)
  reasonId: {type: [Number, String], default: 6},
  note: {type: String, default: ''},
  // ID des offenen Eintrags in student_school_stays (optional)
  stayId: {type: [Number, String, null], default: null},
  // Optionales Standarddatum, z. B. aus der Timeline (.field.date-picker)
  defaultDate: {type: [String, Date, null], default: null}
});

const emit = defineEmits(['close', 'released']);

const submitting = ref(false);
const localOccurredAt = ref(props.occurredAt || new Date().toISOString());

// Gründe aus DB (table release_reason, via Backend-API)
const releaseReasons = ref([]); // [{id,title}]
const selectedReasonId = ref(Number(props.reasonId) || 6);
const noteText = ref(props.note || '');

// Date- und Timepicker
const dateValue = ref(null); // Date-Objekt für Datum
const timeString = ref(nowHHMM());

const toast = useToast();

// Flatpickr Optionen
const dateOpts = {
  dateFormat: 'Y-m-d',
  altInput: true,
  altFormat: 'd.m.Y',
  locale: German,
  allowInput: true
};

const timeOpts = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
  locale: German,
  allowInput: true
};

function nowHHMM() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function asDateOnly(val) {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

watch(
    () => props.open,
    (isOpen) => {
      if (isOpen) {
        // Defaults beim Öffnen setzen
        const ovd = getOvDateAsDate();
        const base =
          (props.defaultDate ? asDateOnly(props.defaultDate) : null) ??
          (ovd ? asDateOnly(ovd) : null) ??
          (props.occurredAt ? asDateOnly(props.occurredAt) : null) ??
          asDateOnly(new Date());

        dateValue.value = base;
        // Uhrzeit immer auf now()
        timeString.value = nowHHMM();

        // localOccurredAt auf das Basisdatum (Mitternacht) setzen; Uhrzeit wird später kombiniert
        const tmp = new Date(base);
        tmp.setHours(0, 0, 0, 0);
        localOccurredAt.value = tmp.toISOString();

        selectedReasonId.value = Number(props.reasonId) || 6;
        noteText.value = props.note || '';
        if (!releaseReasons.value.length) {
          loadReasons();
        }
      }
    }
);

onMounted(() => {
  // Gründe einmalig vorladen (für schnellere erste Öffnung)
  loadReasons();
});

const dateDisplay = computed(() => {
  const d = dateValue.value instanceof Date ? dateValue.value : null;
  if (!d) return '';
  return d.toLocaleDateString('de-DE');
});

function getOvDateAsDate() {
  try {
    const el = document.getElementById('ov-date');
    if (!el) return null;
    const anyEl = el;
    if (anyEl._flatpickr && Array.isArray(anyEl._flatpickr.selectedDates) && anyEl._flatpickr.selectedDates[0]) {
      return anyEl._flatpickr.selectedDates[0];
    }
    const raw = (el.value || '').toString().trim();
    if (!raw) return null;

    // 1) ISO / Date.parse-kompatibel
    const iso = new Date(raw);
    if (!Number.isNaN(iso.getTime())) return iso;

    // 2) dd.mm.yyyy
    const m = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  } catch {
    return null;
  }
}

function normalizeDateValue(val) {
  if (val instanceof Date) {
    return new Date(val);
  }
  if (typeof val === 'string' && val.trim()) {
    const iso = new Date(val);
    if (!Number.isNaN(iso.getTime())) {
      return iso;
    }
    const m = val.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) {
      const [, dd, mm, yyyy] = m;
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      if (!Number.isNaN(d.getTime())) {
        return d;
      }
    }
  }
  return new Date();
}

function getStudentId(s) {
  const id = s?.student_id ?? s?.studentId ?? s?.id ?? null;
  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

// Ableitung einer numerischen stayId aus Prop
function getStayIdRaw() {
  const raw = props.stayId ?? props.student?.stayId ?? null;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
}

const studentName = computed(() => {
  const s = props.student || {};
  return s.student_name || s.studentName || s.name || s.label || '';
});

const currentReasonTitle = computed(() => {
  const id = Number(selectedReasonId.value || 0);
  const match = releaseReasons.value.find(r => Number(r.id) === id);
  if (match && match.title) {
    return match.title;
  }
  // Fallback: ursprüngliche Note / Standardtext
  if (props.note && props.note !== 'Schulschluss') {
    return props.note;
  }
  return 'Schulschluss';
});

// Flatpickr on-change
function onTimeChangeFp(selectedDates, dateStr) {
  timeString.value = (dateStr || '').slice(0, 5);
}

function onDateChangeFp(selectedDates) {
  dateValue.value = selectedDates?.[0] ?? dateValue.value;
}

function onReasonChange() {
  // Reaktiv-Computed currentReasonTitle kümmert sich um den Text
}

async function loadReasons() {
  try {
    // Erwartet, dass das Backend aktive Gründe aus release_reason liefert
    const res = await presenceApi.getReasons?.();
    const raw = res?.data?.data ?? res?.data ?? [];
    const list = Array.isArray(raw) ? raw : [];
    releaseReasons.value = list
        .map(r => ({
          id: Number(r.id),
          title: String(r.title || '').trim() || 'Unbenannter Grund',
        }))
        .filter(r => Number.isFinite(r.id));
  } catch {
    // Fallback: nur Schulschluss (ID 6), falls API nicht erreichbar
    if (!releaseReasons.value.length) {
      releaseReasons.value = [
        {id: 6, title: 'Schulschluss'},
      ];
    }
  }

  // Sicherstellen, dass der Default-Grund verfügbar ist
  if (!releaseReasons.value.some(r => Number(r.id) === Number(selectedReasonId.value))) {
    const six = releaseReasons.value.find(r => Number(r.id) === 6);
    selectedReasonId.value = six ? six.id : (releaseReasons.value[0]?.id ?? 6);
  }
}

function buildOccurredAtFromTime() {
  // Datum aus Datepicker, Uhrzeit aus timeString
  const base = normalizeDateValue(dateValue.value ?? localOccurredAt.value ?? new Date());
  if (Number.isNaN(base.getTime())) {
    return new Date().toISOString();
  }
  const m = (timeString.value || '').match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!m) {
    return base.toISOString();
  }
  const [, hh, mm] = m;
  base.setHours(Number(hh), Number(mm), 0, 0);
  return base.toISOString();
}

async function confirmRelease() {
  const id = getStudentId(props.student);
  if (!id) {
    toast.add({severity: 'error', summary: 'Ungültige Schüler-ID.', life: 3000});
    return;
  }

  const time = (timeString.value || '').trim();
  const validTime = /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
  if (!validTime) {
    toast.add({severity: 'error', summary: 'Bitte eine gültige Uhrzeit im Format HH:MM (24h) angeben.', life: 4000});
    return;
  }

  const when = buildOccurredAtFromTime();
  const rid = Number(selectedReasonId.value || 0) || null;
  const stayId = getStayIdRaw();

  submitting.value = true;
  try {
    await presenceApi.out({
      studentId: id,
      occurredAt: when,
      reasonId: rid,
      reason_id: rid,
      note: noteText.value || null,
      // ID des offenen Eintrags in student_school_stays (optional)
      stayId: stayId ?? undefined
    });
    toast.add({
      severity: 'success',
      summary: `${studentName.value || 'Schüler'} entlassen.`,
      life: 3000,
    });
    emit('released', {
      studentId: id,
      occurredAt: when,
      reasonId: rid,
      note: noteText.value || null,
      stayId: stayId ?? null
    });
    emit('close');
  } catch (e) {
    const err = e?.response?.data?.error || {};
    const msg = err?.message || 'Entlassung fehlgeschlagen.';
    const bo = err?.blockingOffer || null;

    let detail = '';
    if (bo) {
      const subject = (bo.subject || '').toString().trim();
      const topic = (bo.topic || '').toString().trim();
      const title = [subject, topic].filter(Boolean).join(' – ') ;

      let chkInText = '',
          chkOutText = '';

      try {
        if (bo.checkin_at) {
          const dtIn = new Date(bo.checkin_at);
          if (!Number.isNaN(dtIn.getTime())) {
            chkInText = dtIn.toLocaleString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        }
        if (bo.checkout_at) {
          const dtOut = new Date(bo.checkout_at);
          if (!Number.isNaN(dtOut.getTime())) {
            chkOutText = dtOut.toLocaleString('de-DE', {
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        }
      } catch {
        // still
      }

      detail = [
        title ? `Angebot: ${title}` : '',
        chkInText && chkOutText ? `Anwesenheit: ${chkInText} - ${chkOutText}` : ''
      ].filter(Boolean).join('\n');
    }

    toast.add({
      severity: 'error',
      summary: msg,
      detail: detail || undefined,
      life: 12000
    });
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.overlay-dialog {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
}

.overlay-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .35);
}

.dialog-card {
  position: relative;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, .2);
  width: 92%;
  max-width: 460px;
}

.dialog-header {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}

.dialog-body {
  padding: 14px;
}

.dialog-actions {
  padding: 12px 14px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--border);
}

.h2 {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  margin: 0;
}

.btn-warn {
  background: var(--warn);
  color: #fff;
  border: 0;
  padding: 6px 12px;
  border-radius: 8px;
}

.btn-warn .pi {
  color: #fff;
}

/* kleines Formular-Layout im Dialog */
.form-grid {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-textarea .textarea {
  resize: vertical;
  min-height: 52px;
}

.label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 500;
}

.input {
  height: 34px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  font-size: 14px;
}

.textarea {
  height: auto;
}

.input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}
</style>